import { gql, TypedDocumentNode } from '@common/hooks';
import {
  CommentInfoFragment,
  HistoryInfoFragment,
  HistoryInfoFragmentType,
  SupplierHistoryQueryVariables,
  SupplierNamesQueryCgResponse,
  SupplierQueryCgResponse,
  SupplierQueryVariables,
  SupplierReinviteToSignupQueryCgResponse,
  SupplierReinviteToSignupQueryVariables,
} from '@common/graphql';
import { RoleType } from '@clinician-profile/graphql';

export const SUPPLIER_NAMES_QUERY: TypedDocumentNode<SupplierNamesQueryCgResponse> = gql`
  query SupplierNames {
    suppliers {
      id
      name
    }
  }
`;

export const REINVITE_SUPPLIER_TO_SIGNUP_QUERY: TypedDocumentNode<
  SupplierReinviteToSignupQueryCgResponse,
  SupplierReinviteToSignupQueryVariables
> = gql`
  query SupplierReinviteToSignup($id: String!) {
    supplier(id: $id) {
      id
      name
      supplierSignupInvitation {
        id
        email
      }
    }
  }
`;

export type SupplierOrganizationUnitsActiveType =
  SupplierQueryCgResponse['supplier']['organizationUnitsActive'][number];

export type SupplierQueryType = SupplierQueryCgResponse['supplier'];

export type SupplierOrganizationBusinessType =
  SupplierQueryCgResponse['supplier']['organizationBusinesses'][number];

export type SupplierActivationLogForCurrentOrganizationType =
  SupplierQueryCgResponse['supplier']['activationLogForCurrentOrganization'];

export const SUPPLIER_QUERY: TypedDocumentNode<
  SupplierQueryCgResponse,
  SupplierQueryVariables
> = gql`
  query Supplier($id: String!) {
    supplier(id: $id) {
      id
      name
      address
      city
      history {
        ...HistoryInfoFragment
      }
      stateCode
      postalCode
      phoneNumber
      website
      timeZone
      statusForCurrentOrganization {
        id
        status
      }
      canUninvite
      supplierSignupInvitation {
        id
        email
        comments {
          ...CommentInfoFragment
        }
      }
      activationLogForCurrentOrganization {
        id
        lastComment {
          ...CommentInfoFragment
        }
        actionType
        createdAt
        createdBy {
          id
          name
        }
      }
      defaultContact {
        id
        name
        email
        phone
        timeZoneAbbreviation
      }
      contacts {
        id
        contactType
        worksMembership {
          id
          name
          phone
          email
          timeZoneAbbreviation
        }
      }
      organizationBusinesses {
        id
        name
        organizationUnitsActiveCount
      }
      organizationUnitsActive(input: { scope: "organization" }) {
        id
        name
        organizationBusiness {
          id
          name
        }
        activeStaffingRequestApplications(id: $id) {
          id
        }
        activeOffers(id: $id) {
          id
        }
        activeExtensionOffers(id: $id) {
          id
        }
      }
      tierForCurrentOrganization {
        id
        name
      }
    }
  }
  ${CommentInfoFragment}
  ${HistoryInfoFragment}
`;

type SupplierHistoryQueryResponse = {
  supplier: {
    id: string;
    history: HistoryInfoFragmentType[];
  };
};

export const SUPPLIER_HISTORY_QUERY: TypedDocumentNode<
  SupplierHistoryQueryResponse,
  SupplierHistoryQueryVariables
> = gql`
  query SupplierHistory($id: String!) {
    supplier(id: $id) {
      id
      history {
        ...HistoryInfoFragment
      }
    }
  }
  ${HistoryInfoFragment}
`;

export type PaginatedOrganizationRolesQueryResponse = {
  paginatedOrganizationRoles: {
    roles: RoleType[];
    totalCount: number;
  };
};

export type PaginatedOrganizationRolesQueryVariables = {
  limit: number;
  offset: number;
  orderDirection?: string;
  orderField?: string;
  filters?: Record<string, string[] | null>;
  supplierId?: string;
};

export const PAGINATED_ORGANIZATION_ROLES_QUERY: TypedDocumentNode<
  PaginatedOrganizationRolesQueryResponse,
  PaginatedOrganizationRolesQueryVariables
> = gql`
  query PaginatedOrganizationRoles(
    $limit: Int!
    $offset: Int!
    $orderDirection: OrderDirection
    $orderField: OrganizationRoleOrderField
    $filters: OrganizationRoleFilterInput
    $supplierId: ID
  ) {
    paginatedOrganizationRoles(
      limit: $limit
      offset: $offset
      orderDirection: $orderDirection
      orderField: $orderField
      filters: $filters
      supplierId: $supplierId
    ) {
      roles {
        id
        name
        abbreviation
        roleType
        enabledForSupplier(supplierId: $supplierId)
      }

      totalCount
    }
  }
`;
